import React from 'react';
import moment from 'moment';
import i18n from '~/i18n';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import FlagIcon from '@material-ui/icons/Flag';
import StatusDropdown from '../atoms/StatusCodeDropdownV2';
import useKanbanCardStyles from './styles';
import { FullscreenPortalContext } from '../context/FullscreenPortalContext';
import { formatShortDateTime } from '~/utils/moment';

const pad2 = n => String(n).padStart(2, '0');

const minutesToHHMM = (min) => {
  const minutes = Number(min) || 0;
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return `${pad2(h)}:${pad2(m)}`;
};

function formatDateTime(date, time) {
  if (!date || !time) return null;

  const dateAndTime = formatShortDateTime(date, time);

  if (!dateAndTime) return null;

  const [dateFormat, timeFormat] = dateAndTime.split(' ');
  return `${dateFormat} • ${timeFormat}`;
}

export default function KanbanCard({ card, onUpdate, showRibbon }) {
  const { t: translate } = useTranslation();
  const fullscreenContainer = React.useContext(FullscreenPortalContext);
  const classes = useKanbanCardStyles();

  const {
    element_name = 'F290',
    element_point_name = 'N-838',
    code_ug_name = 'Ag. Liberação',
    id_code_ug = 1,
    unitary_operation_name = null,
    priority = 0,
    highlight_level = 0,
    statusColor = '#2e7d32',
    borderColor = '#ef5350',
    code_time = 2134,
    cicle = '01',
    // timestamp = null,
    actual_date = '25/09/2025',
    time = '21:12:45',
    showPlay = false,
    // showFlag = true,
  } = card || {};

  const priorityOptions = [
    { value: 0, label: 'Priori.: 0', color: '#942121' },
    { value: 1, label: 'Priori.: 1', color: '#B32828' },
    { value: 2, label: 'Priori.: 2', color: '#D32F2F' },
    { value: 3, label: 'Priori.: 3', color: '#DC5959' },
    { value: 4, label: 'Priori.: 4', color: '#FF9800' },
    { value: 5, label: 'Priori.: 5', color: '#FFAD33' },
    { value: 6, label: 'Priori.: 6', color: '#FFCF33' },
    { value: 7, label: 'Priori.: 7', color: '#239D56' },
    { value: 8, label: 'Priori.: 8', color: '#27AE60' },
    { value: 9, label: 'Priori.: 9', color: '#52BE80' },
  ];

  const prioritySelected = priorityOptions.find(o => o.value === priority);

  const codeTypesColors = {
    0: '#ef6c00',
    1: '#2e7d32',
  };

  const chipStyle = {
    background: prioritySelected?.color || '#9e9e9e',
    color: '#fff',
    height: 28,
    borderRadius: 14,
    fontWeight: 700,
    maxWidth: '105px',
  };

  const codes = useSelector(state => state.underground.codes);
  const codeGroups = useSelector(state => state.underground.codeGroups);
  const codeGroupsById = codeGroups.reduce((acc, codeGroup) => {
    acc[codeGroup.id] = codeGroup;
    return acc;
  }, {});

  const codesOptions = codes.map(
    (c) => {
      const findCodeGroup = codeGroupsById[c.id_code_group_ug] || {};
      const codeGroupCategory = findCodeGroup?.category;
      return ({ label: c.name, value: c.id, color: codeTypesColors[codeGroupCategory] });
    },
  ).sort(
    (a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }),
  );

  const favoriteOptions = [
    {
      value: 0,
      label: translate('common:None'),
      color: '#858585',
      icon: <FlagIcon style={{ color: '#858585' }} />,
    },
    {
      value: 1,
      label: translate('common:Detonation'),
      color: '#D32F2F',
      icon: <FlagIcon style={{ color: '#D32F2F' }} />,
    },
    {
      value: 2,
      label: translate('common:Attention'),
      color: '#FF9800',
      icon: <FlagIcon style={{ color: '#FF9800' }} />,
    },
    {
      value: 3,
      label: translate('common:Favorite'),
      color: '#1976D2',
      icon: <FlagIcon style={{ color: '#1976D2' }} />,
    },
  ];

  const cardContainerBorderColor = {
    0: '#858585',
    1: '#D32F2F',
    2: '#FF9800',
    3: '#1976D2',
  };

  return (
    <div className={classes.cardContainer}>
      { showRibbon
        && (
          <div className={classes.ribbon}>
            <div
              className={classes.ribbonHeader}
              style={{
                background: `${cardContainerBorderColor[highlight_level] || '#A3A3A3'}`,
              }}
            >
              {unitary_operation_name}
            </div>
          </div>
        )
      }

      <Card
        className={classes.cardStyle}
        style={{
          border: showRibbon ? `2px solid ${cardContainerBorderColor[highlight_level]}` : '2px solid #A3A3A3',
        }}
      >
        <CardContent className={classes.contentStyle}>
          <div className={classes.topBar}>
            <StatusDropdown
              value={id_code_ug}
              options={codesOptions}
              onChange={next => onUpdate({ id_code_ug: next })}
              variant="chip"
              showSearch
              menuContainer={fullscreenContainer}
              // showSelectedHeader={true}
              // showSelectedHeader={false}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
            />
            <div className={classes.ribbonGroup}>
              {/* <Chip
                style={chipStyle}
                label={priorityOptions[priority]?.label}
              /> */}
              <StatusDropdown
                value={priority}
                options={priorityOptions}
                onChange={next => onUpdate({ priority: next })}
                variant="chip"
                showSearch={false}
                showSelectedHeader={false}
                isOpen={false}
              />
              {showPlay ? (
                <PlayArrowIcon
                  className={classes.iconPlayStyle}
                  onClick={() => onUpdate && onUpdate({ showPlay: false })}
                />
              ) : null}
            </div>
          </div>

          <div className={classes.titleRow}>
            <Typography variant="subtitle1">
              {/* {element_name}
              {' | '} */}
              {element_point_name}
            </Typography>
          </div>

          <Divider />

          <Typography className={classes.metaRow}>
            {code_ug_name}
            <AccessTimeIcon
              style={{ fontSize: 16, verticalAlign: 'middle' }}
            />
            {' '}
            {minutesToHHMM(code_time)}
          </Typography>

          <Box className={classes.rowContainer} mt={1}>
            <Box className={classes.cicleInfoContainer}>
              <Typography className={classes.infoText}>
                {translate('common:Cycles')}
              </Typography>
              <Typography className={classes.indexBadge}>
                {cicle}
              </Typography>
            </Box>
            <Box className={classes.dateInfoContainer}>
              <Typography className={classes.infoText}>
                {translate('common:StartDateAndTime')}
              </Typography>
              <Typography className={classes.dateContainer}>
                {formatDateTime(actual_date, time)}
              </Typography>
            </Box>

            <div />
            <StatusDropdown
              value={highlight_level}
              options={favoriteOptions}
              onChange={next => onUpdate({ highlight_level: next })}
              variant="icon"
              triggerIcon={<FlagIcon />}
              ariaLabel="Selecionar bandeira"
              showSearch={false}
              menuContainer={fullscreenContainer}
              // showSelectedHeader={false}
              // headerLabel="Nenhum"
            />
          </Box>
        </CardContent>
      </Card>
    </div>
  );
}

const cardShape = PropTypes.shape({
  element_name: PropTypes.string,
  element_point_name: PropTypes.string,
  code_ug_name: PropTypes.string,
  id_code_ug: PropTypes.number,
  categoryLabel: PropTypes.string,
  priority: PropTypes.number,
  highlight_level: PropTypes.number,
  statusColor: PropTypes.string,
  borderColor: PropTypes.string,
  code_time: PropTypes.number,
  cicle: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  actual_date: PropTypes.string,
  time: PropTypes.string,
  showPlay: PropTypes.bool,
  showFlag: PropTypes.bool,
});

KanbanCard.propTypes = {
  card: cardShape.isRequired,
  onUpdate: PropTypes.func,
  showRibbon: PropTypes.bool,
};
KanbanCard.defaultProps = {
  onUpdate: () => {},
  showRibbon: false,
};
